<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  modelValue: number
  min?: number
  max?: number
  step?: number
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const minValue = computed(() => props.min ?? 1)
const maxValue = computed(() => props.max ?? 22)
const stepValue = computed(() => props.step ?? 0.5)

function clamp(value: number): number {
  if (Number.isNaN(value)) return minValue.value
  return Math.min(Math.max(value, minValue.value), maxValue.value)
}

function onInput(event: Event) {
  const target = event.target as HTMLInputElement
  const next = Number(target.value)
  if (target.value === '' || Number.isNaN(next)) return
  emit('update:modelValue', clamp(next))
}

function onBlur(event: Event) {
  const target = event.target as HTMLInputElement
  const next = clamp(Number(target.value))
  emit('update:modelValue', next)
  target.value = String(next)
}
</script>

<template>
  <div class="charge-rate-input">
    <div class="charge-rate-input__label-row">
      <label class="charge-rate-input__label" for="charge-rate-input-field">
        Bilens ladehastighed
      </label>
      <span class="charge-rate-input__tooltip">
        <button
          type="button"
          class="charge-rate-input__info"
          aria-describedby="charge-rate-tooltip"
          aria-label="Mere information om ladehastighed"
        >
          i
        </button>
        <span id="charge-rate-tooltip" role="tooltip" class="charge-rate-input__tooltip-body">
          11 kW svarer til en typisk 3-faset hjemmeladestander. Tjek bilens specifikation hvis du er
          i tvivl.
        </span>
      </span>
    </div>
    <div class="charge-rate-input__field">
      <input
        id="charge-rate-input-field"
        class="charge-rate-input__input"
        type="number"
        inputmode="decimal"
        :min="minValue"
        :max="maxValue"
        :step="stepValue"
        :value="modelValue"
        aria-label="Ladehastighed i kilowatt"
        @input="onInput"
        @blur="onBlur"
      />
      <span class="charge-rate-input__suffix" aria-hidden="true">kW</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.charge-rate-input {
  display: flex;
  flex-direction: column;
  gap: space(2);

  &__label-row {
    display: flex;
    align-items: center;
    gap: space(2);
  }

  &__label {
    font-weight: $font-weight-semibold;
    color: color('ink');
    font-size: $font-size-sm;
  }

  &__tooltip {
    position: relative;
    display: inline-flex;
  }

  &__info {
    width: 18px;
    height: 18px;
    padding: 0;
    background: color('background-light');
    border: 1px solid color('background-medium');
    border-radius: 50%;
    color: color('ink-light');
    font-size: 11px;
    font-weight: $font-weight-bold;
    font-style: italic;
    font-family: $font-family-base;
    line-height: 1;
    cursor: help;
    transition:
      background $duration-base $easing-standard,
      color $duration-base $easing-standard;

    &:hover,
    &:focus-visible {
      background: color('accent');
      color: color('white');
      border-color: color('accent');
      outline: none;
    }
  }

  &__tooltip-body {
    position: absolute;
    bottom: calc(100% + space(2));
    left: 50%;
    z-index: 10;
    width: 240px;
    padding: space(3);
    background: color('primary');
    color: color('white');
    font-size: $font-size-xs;
    font-weight: $font-weight-regular;
    line-height: $line-height-snug;
    border-radius: $radius-md;
    box-shadow: $shadow-md;
    transform: translateX(-50%) translateY(4px);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      opacity $duration-base $easing-standard,
      transform $duration-base $easing-standard,
      visibility $duration-base $easing-standard;

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      width: 0;
      height: 0;
      border: 6px solid transparent;
      border-top-color: color('primary');
      transform: translateX(-50%);
    }
  }

  &__info:hover + &__tooltip-body,
  &__info:focus-visible + &__tooltip-body,
  &__tooltip-body:hover {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }

  &__field {
    display: inline-flex;
    align-items: center;
    gap: space(2);
    width: fit-content;
    padding: space(1) space(3);
    background: color('white');
    border: 1px solid color('background-medium');
    border-radius: $radius-md;
    transition: border-color $duration-base $easing-standard;

    &:focus-within {
      border-color: color('accent');
      box-shadow: 0 0 0 3px rgb(0, 176, 62, 0.15);
    }
  }

  &__input {
    width: 4.5rem;
    padding: space(1) 0;
    background: transparent;
    border: none;
    outline: none;
    font-family: inherit;
    font-size: $font-size-md;
    font-weight: $font-weight-bold;
    color: color('primary');

    // Hide spinner buttons in webkit; we keep keyboard arrow stepping.
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      appearance: none;
      margin: 0;
    }

    appearance: textfield;
  }

  &__suffix {
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
    color: color('ink-light');
  }

  &__hint {
    margin: 0;
    font-size: $font-size-xs;
    color: color('ink-light');
    font-style: italic;
  }
}
</style>
